import { ColorPicker, Input, Label, PageTabsContent, Textarea } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { DataAccessTable } from "./DataAccessTable";
import { RoleEditor } from "./useEditRoleDialog";

const PRESET_COLORS = [
  "#3B82F6",
  "#10B981",
  "#8B5CF6",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#84CC16",
  "#EC4899",
  "#F97316",
  "#6366F1",
];

export interface RoleSettingsTabContentProps {
  editor: RoleEditor;
}

export const RoleSettingsTabContent = ({ editor }: RoleSettingsTabContentProps) => {
  const { t } = useTranslation("settings");
  const { draft, setRoleName, setColor, setDescription, isRenamed } = editor;

  return (
    <PageTabsContent
      value="settings"
      className="min-w-0 px-6 py-4 space-y-6 overflow-y-auto"
      style={{ maxHeight: "min(80vh, 900px)" }}
    >
      <div className="space-y-2 max-w-sm">
        <Label htmlFor="role-name">{t("Role name")}</Label>
        <Input
          id="role-name"
          value={draft.role}
          onChange={(e) => setRoleName(e.target.value)}
          placeholder={t("e.g., Care Coordinator")}
        />
        {isRenamed && (
          <p className="text-xs text-amber-600">
            {t("Renaming this role will update all assigned users and log them out.")}
          </p>
        )}
      </div>

      <div className="space-y-2 max-w-sm">
        <Label htmlFor="role-description">{t("Description")}</Label>
        <Textarea
          id="role-description"
          value={draft.description ?? ""}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("Optional description for this role")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="role-color">{t("Color")}</Label>
        <ColorPicker
          id="role-color"
          value={draft.color ?? ""}
          onValueChange={setColor}
          presetColors={PRESET_COLORS}
          swatchClassName="rounded-sm"
          clearable
        />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">{t("Data Access")}</h3>
        <p className="text-xs text-muted-foreground">
          {t("Control create, read, update, and delete access per feature, and whether each appears in the sidebar.")}
        </p>
        <DataAccessTable editor={editor} />
      </div>
    </PageTabsContent>
  );
};
